import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { LoadTypes } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';
import { MessageService } from 'primeng/api';
import { DateTime } from 'luxon';


@Component({
  selector: 'app-new-order-dialog',
  templateUrl: './new-order-dialog.component.html',
  styleUrl: './new-order-dialog.component.scss'
})
export class NewOrderDialogComponent {

  constructor(private dataServiceService: DataServiceService, private messageService: MessageService) { }
  @Output() orderCreated = new EventEmitter<void>();

  @Input() customers: any[] = [];
  @Input() receiverOptions: any[] = [];
  @Input() supplierOptions: any[] = [];
  @Input() trailers: any[] = [];
  @Input() loadTypesArray: LoadTypes[] = [];
  @Input() loadType: any[] = [];
  @Input() isForTender: boolean = false;
  @Input() tenderPosition: 'left' | 'center' | 'right' = 'center';
  @Input() tenderCounter: number = 0;
  @Input() tenderTotal: number = 0;


  visible: boolean = false;
  isExternalTrailer: boolean = false;
  saving: boolean = false;
  orderQuantity: number = 1;
  currentLoadIndex: number = 1;

  defaultOrderState: any = {
    order_id: '',
    trailer_id: '',
    external_trailer_number: '',
    supplier_id: '',
    receiver_id: '',
    supplier_address_id: '',
    receiver_address_id: '',
    supplier_selection: '',
    receiver_selection: '',
    date_received: new Date().toLocaleString('en-US'),
    pickup_driver_id: '',
    pickup_date: '',
    delivery_driver_id: '',
    delivery_date: '',
    load_type: '',
    load_status: 'Unassigned',
    pickup_number: '',
    notes: '',
    time_loaded: '',
    time_unloaded: '',
    time_delivered: ''
  };

  newOrder: any = { ...this.defaultOrderState };
  currentReceiverOptions: any[] = [];
  currentSupplierOptions: any[] = [];

  showPickupTimePicker: boolean = false;
  showDeliveryTimePicker: boolean = false;

  private messageId: string = '';
  private tenderHtml: string = '';

  // Filtered customer arrays for dropdowns
  get supplierCustomers() {
    return this.customers.filter(customer =>
      customer.customer_type === 'Supplier' || customer.customer_type === 'Both'
    );
  }

  get receiverCustomers() {
    return this.customers.filter(customer =>
      customer.customer_type === 'Receiver' || customer.customer_type === 'Both'
    );
  }

  getReceiversForSupplier(supplier_id: number): any[] {
    if (!supplier_id) {
      // No supplier selected - return all receiver options
      return this.receiverOptions;
    }

    const supplier = this.customers.find(c => c.customer_id === supplier_id);
    if (supplier && supplier.receivers && supplier.receivers.length > 0) {
      console.log('Supplier', supplier.receivers);
      const receiverOptions: any[] = [];

      for (const receiver of supplier.receivers) {
        // Find the full customer data for this receiver
        const receiverCustomer = this.customers.find(c => c.customer_id === receiver.customer_id);

        if (receiverCustomer) {
          if (receiverCustomer.addresses && receiverCustomer.addresses.length > 0) {
            // Multiple addresses: create one option per address with compact format
            receiverCustomer.addresses.forEach((address: any) => {
              receiverOptions.push({
                name: `${receiverCustomer.name} (${address.city}, ${address.state})`,
                customer_id: receiverCustomer.customer_id,
                address_id: address.address_id,
                value: `${receiverCustomer.customer_id}_${address.address_id}`
              });
            });
          } else {
            // Single/no address: just the customer name
            receiverOptions.push({
              name: receiverCustomer.name,
              customer_id: receiverCustomer.customer_id,
              address_id: null,
              value: receiverCustomer.customer_id.toString()
            });
          }
        }
      }

      console.log('Receiver Options', receiverOptions);
      return receiverOptions;
    }
    // If supplier has no specific receivers, return all receiver options
    return this.receiverOptions;
  }

  // Handle supplier selection (combined customer+address)
  onSupplierChange(selectedValue: string) {
    if (!selectedValue) {
      // No supplier selected - show all receiver options
      this.currentReceiverOptions = this.receiverOptions;
      this.currentSupplierOptions = this.supplierOptions;
      this.newOrder.supplier_id = null;
      this.newOrder.supplier_address_id = null;
      this.newOrder.receiver_selection = '';  // Reset receiver selection
      return;
    }

    if (selectedValue.includes('_')) {
      const parts = selectedValue.split('_');
      this.newOrder.supplier_id = parseInt(parts[0]);
      this.newOrder.supplier_address_id = parseInt(parts[1]);
    } else {
      this.newOrder.supplier_id = parseInt(selectedValue);
      this.newOrder.supplier_address_id = null;
    }

    // Cache the receiver options instead of calling the method in template
    this.currentReceiverOptions = this.getReceiversForSupplier(this.newOrder.supplier_id);
    this.newOrder.receiver_selection = '';  // Reset receiver selection when supplier changes
  }

  // Handle receiver selection (combined customer+address)
  onReceiverChange(selectedValue: string) {
    if (selectedValue.includes('_')) {
      const parts = selectedValue.split('_');
      this.newOrder.receiver_id = parseInt(parts[0]);
      this.newOrder.receiver_address_id = parseInt(parts[1]);
    } else {
      this.newOrder.receiver_id = parseInt(selectedValue);
      this.newOrder.receiver_address_id = null;
    }
  }

  createOrder() {
    this.saving = true;
    const orderToAdd = {
      trailer_id: null,
      external_trailer_number: this.isExternalTrailer ? this.newOrder.external_trailer_number : '',
      supplier_id: Number(this.newOrder.supplier_id),
      receiver_id: Number(this.newOrder.receiver_id),
      supplier_address_id: this.newOrder.supplier_address_id ? Number(this.newOrder.supplier_address_id) : null,
      receiver_address_id: this.newOrder.receiver_address_id ? Number(this.newOrder.receiver_address_id) : null,
      date_received: new Date().toISOString(),
      load_type: this.newOrder.load_type,
      load_status: 'Unassigned',
      pickup_driver_id: this.newOrder.pickup_driver_id ? Number(this.newOrder.pickup_driver_id) : null,
      pickup_date: this.newOrder.pickup_date ? this.convertCentralToUTC(this.newOrder.pickup_date) : null,
      delivery_driver_id: this.newOrder.delivery_driver_id ? Number(this.newOrder.delivery_driver_id) : null,
      delivery_date: this.newOrder.delivery_date ? this.convertCentralToUTC(this.newOrder.delivery_date) : null,
      time_loaded: this.newOrder.time_loaded ? this.convertCentralToUTC(this.newOrder.time_loaded) : null,
      time_unloaded: this.newOrder.time_unloaded ? this.convertCentralToUTC(this.newOrder.time_unloaded) : null,
      time_delivered: this.newOrder.time_delivered ? this.convertCentralToUTC(this.newOrder.time_delivered) : null,
      pickup_number: this.newOrder.pickup_number || null,
      notes: this.newOrder.notes || null,
      is_tender: this.isForTender,
      message_id: this.messageId,
      html: this.isForTender ? this.tenderHtml : null
    };

    this.dataServiceService.createOrder(orderToAdd).subscribe({
      next: (response) => {
        this.saving = false;
        this.orderCreated.emit(response);

        if (this.orderQuantity > 1 && this.currentLoadIndex < this.orderQuantity) {
          this.currentLoadIndex++;
          this.clearPerLoadFields();
          this.messageService.add({
            severity: 'info',
            summary: 'Load Created',
            detail: `Load ${this.currentLoadIndex - 1} of ${this.orderQuantity} created`
          });
        } else {
          const completedQuantity = this.orderQuantity;
          this.resetForm();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: completedQuantity > 1
              ? `All ${completedQuantity} orders were added successfully`
              : 'Order was added successfully'
          });
        }
      },
      error: (error) => {
        this.saving = false;
        console.error('Error adding order:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add order'
        });
      }
    });
  }

  // Separate method for form reset
  resetForm() {
    this.newOrder = { ...this.defaultOrderState };
    this.visible = false;
    this.messageId = '';
    this.orderQuantity = 1;
    this.currentLoadIndex = 1;
    this.showPickupTimePicker = false;
    this.showDeliveryTimePicker = false;
  }

  isMidMultiLoad(): boolean {
    return this.currentLoadIndex > 1;
  }

  clearPerLoadFields() {
    this.newOrder.pickup_date = '';
    this.newOrder.delivery_date = '';
    this.newOrder.pickup_number = '';
    this.showPickupTimePicker = false;
    this.showDeliveryTimePicker = false;
  }

  // Add this method to NewOrderDialogComponent
  handleTender(tender: any) {
    this.tenderCounter += 1;
    // Reset form to default state first
    this.newOrder = { ...this.defaultOrderState };
    // Note: isForTender is set via input property
    this.messageId = tender.messageId;
    this.tenderHtml = tender.htmlContent;

    // Set the pickup number from tender
    this.newOrder.pickup_number = tender.extracted_info.order_number;

    // Find matching supplier
    const supplierMatch = this.findMatchingCustomer(tender.extracted_info.start_location, 'Supplier');
    if (supplierMatch) {
      this.newOrder.supplier_selection = supplierMatch.addressId ?
        `${supplierMatch.customerId}_${supplierMatch.addressId}` :
        supplierMatch.customerId;

      // Update dependent fields
      this.onSupplierChange(this.newOrder.supplier_selection);
    }

    // Find matching receiver
    const receiverMatch = this.findMatchingCustomer(tender.extracted_info.final_del_location, 'Receiver');
    if (receiverMatch) {
      this.newOrder.receiver_selection = receiverMatch.addressId ?
        `${receiverMatch.customerId}_${receiverMatch.addressId}` :
        receiverMatch.customerId;

      // Update dependent fields
      this.onReceiverChange(this.newOrder.receiver_selection);
    }

    // Show the dialog
    this.visible = true;
  }

  // Move the findMatchingCustomer method from TenderDialog to here since it's now more related to this component
  private findMatchingCustomer(locationName: string, type: 'Supplier' | 'Receiver'): { customerId: string, addressId?: string } | null {
    // Clean up location name for matching
    const cleanLocation = locationName.toLowerCase()
      .replace(/inc|llc|corp|bulk only/g, '')  // Remove common business terms
      .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
      .trim();

    // Find matching customer
    const match = this.customers.find(customer => {
      if (customer.customer_type !== type && customer.customer_type !== 'Both') {
        return false;
      }

      const cleanCustomerName = customer.name.toLowerCase()
        .replace(/inc|llc|corp/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim();

      return cleanCustomerName.includes(cleanLocation) ||
        cleanLocation.includes(cleanCustomerName);
    });

    if (match) {
      // If customer has addresses, use the first one
      if (match.addresses && match.addresses.length > 0) {
        return {
          customerId: match.customer_id.toString(),
          addressId: match.addresses[0].address_id.toString()
        };
      }

      return {
        customerId: match.customer_id.toString()
      };
    }

    return null;
  }

  // Convert local date to Central Time, then to UTC for database storage
  private convertCentralToUTC(date: Date | string): string {
    const centralDate = DateTime.fromJSDate(new Date(date), { zone: 'America/Chicago' });
    return centralDate.setZone('utc').toISO() as string;
  }
}
